import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  setDoc
} from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Edit2, Ban, ChevronLeft } from "lucide-react";
import { AppSidebar } from "@/components/app-sidebar";
import { Switch } from "@/components/ui/switch";
import { createUserWithoutSignIn } from "@/lib/userManagement";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  getPendingInvitations,
  activateInvitation,
  rejectInvitation,
  Invitation
} from "@/services/invitationService";
import { awardInvitationPoints } from "@/services/scoreService";
import { getUserName } from "@/services/userService";
import { InvitationActivationDialog } from "@/components/InvitationActivationDialog";

// Force cache bust for UserManagement - updated for latest deployment

// Add position options constant
const POSITION_OPTIONS = [
  { value: "praktikant", label: "Praktikant" },
  { value: "dual-student", label: "Dualer Student" },
  { value: "beraterassistent", label: "Beraterassistent" },
  { value: "trainee", label: "Trainee" },
  { value: "berater", label: "Berater" }
];

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  position?: string;
  createdAt: string;
  active?: boolean;
  status?: 'active' | 'inactive' | 'invited';
  invitedBy?: string;
}

// Add password validation function
const validatePassword = (password: string): { isValid: boolean; message?: string } => {
  if (password.length < 8) {
    return { isValid: false, message: "Das Passwort muss mindestens 8 Zeichen lang sein." };
  }

  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: "Das Passwort muss mindestens einen Großbuchstaben enthalten." };
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, message: "Das Passwort muss mindestens ein Sonderzeichen enthalten." };
  }

  return { isValid: true };
};

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // New user form state
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [newPosition, setNewPosition] = useState("");

  // Edit user form state
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [inviterNames, setInviterNames] = useState<Record<string, string>>({});
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [isActivatingInvitation, setIsActivatingInvitation] = useState(false);

  const { currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
    fetchPendingInvitations();
  }, []);

  // Refetch users when showInactiveUsers changes
  useEffect(() => {
    fetchUsers();
  }, [showInactiveUsers]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersCollection = collection(db, "users");
      let usersQuery;

      // For legacy tenant, fetch all users and filter client-side
      // For new tenants, filter by tenantId
      if (currentUser?.tenantId === "legacy") {
        usersQuery = usersCollection;
      } else if (currentUser?.tenantId) {
        usersQuery = query(usersCollection, where("tenantId", "==", currentUser.tenantId));
      } else {
        usersQuery = usersCollection;
      }

      const usersSnapshot = await getDocs(usersQuery);
      const usersList = usersSnapshot.docs.map(doc => {
        const data = doc.data() as any;
        return {
          id: doc.id,
          name: data.name || "",
          email: data.email || "",
          role: data.role || "user",
          position: data.position || "",
          createdAt: data.createdAt || new Date().toISOString(),
          active: data.active !== false,
          status: data.status || (data.active !== false ? 'active' : 'inactive'),
          invitedBy: data.invitedBy,
          tenantId: data.tenantId
        };
      }).filter(user => {
        // For legacy tenant, show users without tenantId or with tenantId="legacy"
        if (currentUser?.tenantId === "legacy") {
          if (user.tenantId && user.tenantId !== "legacy") {
            return false; // Exclude users from other tenants
          }
        }

        // Hide developers from admin users, but developers can see everyone
        if (currentUser?.role === "admin" && user.role === "developer") {
          return false;
        }

        // Filter inactive users unless showInactiveUsers is enabled
        if (!showInactiveUsers && !user.active) {
          return false;
        }

        return true;
      });
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Benutzer konnten nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const invitations = await getPendingInvitations();
      setPendingInvitations(invitations);

      // Fetch inviter names
      const inviterIds = [...new Set(invitations.map(inv => inv.inviterUserId))];
      const names: Record<string, string> = {};

      for (const inviterId of inviterIds) {
        try {
          const inviterName = await getUserName(inviterId);
          names[inviterId] = inviterName;
        } catch (error) {
          console.error("Error fetching inviter name:", error);
          names[inviterId] = "Unbekannter Benutzer";
        }
      }

      setInviterNames(names);
    } catch (error) {
      console.error("Error fetching pending invitations:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Einladungen konnten nicht geladen werden.",
      });
    }
  };

  const handleCreateUser = async () => {
    if (!newName || !newEmail || !newPassword) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Bitte füllen Sie alle Felder aus.",
      });
      return;
    }

    // Validate password
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      toast({
        variant: "destructive",
        title: "Ungültiges Passwort",
        description: passwordValidation.message,
      });
      return;
    }

    setIsCreatingUser(true);

    try {
      // Use our custom function to create a user without logging in
      await createUserWithoutSignIn(
        newEmail,
        newPassword,
        newName,
        newRole,
        newPosition
      );

      toast({
        title: "Erfolg",
        description: "Benutzer wurde erfolgreich erstellt.",
      });

      // Reset form and close dialog
      setNewName("");
      setNewEmail("");
      setNewPassword("");
      setNewRole("user");
      setNewPosition("");
      setNewUserOpen(false);

      // Refresh user list
      fetchUsers();
    } catch (error: any) {
      console.error("Error creating user:", error);
      let errorMessage = "Fehler beim Erstellen des Benutzers.";

      // Handle Firebase Auth REST API error messages
      if (error.message.includes("EMAIL_EXISTS")) {
        errorMessage = "Diese E-Mail wird bereits verwendet.";
      } else if (error.message.includes("INVALID_EMAIL")) {
        errorMessage = "Ungültige E-Mail-Adresse.";
      } else if (error.message.includes("WEAK_PASSWORD")) {
        errorMessage = "Das Passwort erfüllt nicht die Sicherheitsanforderungen.";
      }

      toast({
        variant: "destructive",
        title: "Fehler",
        description: errorMessage,
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !editName) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Ungültige Eingaben.",
      });
      return;
    }

    try {
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, {
        name: editName,
        role: editRole,
        position: editPosition,
        active: editActive
      });

      toast({
        title: "Erfolg",
        description: "Benutzer wurde erfolgreich aktualisiert.",
      });

      setEditUserOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Benutzer konnte nicht aktualisiert werden.",
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    // Don't allow deactivating yourself
    if (selectedUser.id === currentUser?.id) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Sie können Ihren eigenen Account nicht deaktivieren.",
      });
      setDeleteUserOpen(false);
      return;
    }

    const isActivating = selectedUser.active === false;

    try {
      // Update user document to toggle active state
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, {
        active: isActivating
      });

      toast({
        title: isActivating ? "Benutzer aktiviert" : "Benutzer deaktiviert",
        description: isActivating
          ? "Der Benutzer wurde erfolgreich aktiviert und kann sich wieder anmelden."
          : "Der Benutzer wurde erfolgreich deaktiviert und kann sich nicht mehr anmelden.",
      });

      setDeleteUserOpen(false);
      fetchUsers();
    } catch (error) {
      console.error(`Error ${isActivating ? 'activating' : 'deactivating'} user:`, error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: `Benutzer konnte nicht ${isActivating ? 'aktiviert' : 'deaktiviert'} werden.`,
      });
    }
  };

  const openActivationDialog = (invitation: Invitation) => {
    setSelectedInvitation(invitation);
    setActivationDialogOpen(true);
  };

  const handleActivateInvitation = async (invitation: Invitation, password: string) => {
    setIsActivatingInvitation(true);

    try {
      // Create user account with provided password
      const createdUserId = await createUserWithoutSignIn(
        invitation.inviteeEmail,
        password,
        invitation.inviteeName,
        "user", // Default role
        invitation.inviteePosition
      );

      // Activate the invitation
      const success = await activateInvitation(invitation.id, createdUserId, currentUser?.id || "");

      if (success) {
        // Award points to the inviter
        const pointsAwarded = await awardInvitationPoints(
          invitation.inviterUserId,
          invitation.pointsAmount,
          invitation.inviteePosition
        );

        if (pointsAwarded) {
          toast({
            title: "Einladung aktiviert",
            description: `Benutzer wurde erstellt und ${invitation.pointsAmount} Punkte wurden vergeben.`,
          });
        } else {
          toast({
            title: "Einladung aktiviert",
            description: "Benutzer wurde erstellt, aber Punkte konnten nicht vergeben werden.",
            variant: "destructive",
          });
        }

        // Close dialog and refresh data
        setActivationDialogOpen(false);
        setSelectedInvitation(null);
        fetchUsers();
        fetchPendingInvitations();
      } else {
        toast({
          variant: "destructive",
          title: "Fehler",
          description: "Einladung konnte nicht aktiviert werden.",
        });
      }
    } catch (error: any) {
      console.error("Error activating invitation:", error);
      let errorMessage = "Fehler beim Aktivieren der Einladung.";

      if (error.message?.includes("EMAIL_EXISTS")) {
        errorMessage = "Ein Benutzer mit dieser E-Mail-Adresse existiert bereits.";
      }

      toast({
        variant: "destructive",
        title: "Fehler",
        description: errorMessage,
      });
      throw error; // Re-throw so dialog knows to stay open
    } finally {
      setIsActivatingInvitation(false);
    }
  };

  const handleRejectInvitation = async (invitation: Invitation) => {
    try {
      const success = await rejectInvitation(invitation.id, "Von Administrator abgelehnt");

      if (success) {
        toast({
          title: "Einladung abgelehnt",
          description: "Die Einladung wurde erfolgreich abgelehnt.",
        });

        fetchPendingInvitations();
      } else {
        toast({
          variant: "destructive",
          title: "Fehler",
          description: "Einladung konnte nicht abgelehnt werden.",
        });
      }
    } catch (error) {
      console.error("Error rejecting invitation:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Fehler beim Ablehnen der Einladung.",
      });
    }
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setEditName(user.name);
    setEditRole(user.role);
    setEditPosition(user.position || "");
    setEditActive(user.active !== false);
    setEditUserOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteUserOpen(true);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="py-4 px-6 border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold">Nutzerverwaltung</h1>
              <p className="text-sm text-muted-foreground">Verwalten Sie alle Benutzerkonten für die Anwendung</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-inactive"
                  checked={showInactiveUsers}
                  onCheckedChange={setShowInactiveUsers}
                />
                <Label htmlFor="show-inactive" className="text-sm">
                  Inaktive Benutzer anzeigen
                </Label>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={() => setNewUserOpen(true)}
                className="flex gap-2 items-center"
              >
                <UserPlus size={16} />
                <span>Neuer Benutzer</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <Card className="border-0 shadow-none">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <span className="text-muted-foreground">Laden...</span>
                </div>
              ) : (
                <Table>
                  <TableCaption>Eine Liste aller Benutzer im System</TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Erstellt am</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} className={!user.active ? "opacity-60" : ""}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {POSITION_OPTIONS.find(p => p.value === user.position)?.label || "-"}
                        </TableCell>
                        <TableCell>
                          {user.role === "admin" ? "Administrator" : "Benutzer"}
                        </TableCell>
                        <TableCell>
                          {user.active ?
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Aktiv
                            </span> :
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Deaktiviert
                            </span>
                          }
                        </TableCell>
                        <TableCell>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(user)}
                                >
                                  <Edit2 size={16} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Benutzer bearbeiten</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openDeleteDialog(user)}
                                >
                                  <Ban size={16} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{user.active ? "Benutzer deaktivieren" : "Benutzer aktivieren"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center">
                          Keine Benutzer gefunden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pending Invitations Section */}
          {pendingInvitations.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Ausstehende Einladungen</CardTitle>
                <CardDescription>
                  Von Praktikanten gesendete Einladungen, die auf Ihre Genehmigung warten
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Eingeladen von</TableHead>
                      <TableHead>Punkte</TableHead>
                      <TableHead>Datum</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingInvitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell className="font-medium">
                          {invitation.inviteeName}
                        </TableCell>
                        <TableCell>{invitation.inviteeEmail}</TableCell>
                        <TableCell>
                          {POSITION_OPTIONS.find(p => p.value === invitation.inviteePosition)?.label || invitation.inviteePosition}
                        </TableCell>
                        <TableCell>
                          {inviterNames[invitation.inviterUserId] || "Lädt..."}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className="text-yellow-600 font-semibold">
                              {invitation.pointsAmount}
                            </span>
                            <span className="text-xs text-muted-foreground">Punkte</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(invitation.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => openActivationDialog(invitation)}
                                >
                                  Aktivieren
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Einladung genehmigen und Benutzer erstellen</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleRejectInvitation(invitation)}
                                >
                                  Ablehnen
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Einladung ablehnen</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
            <DialogDescription>
              Erstellen Sie ein neues Benutzerkonto mit Anmeldedaten.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder=""
              />
              <p className="text-xs text-muted-foreground">
                Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Großbuchstaben sowie ein Sonderzeichen enthalten.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="position">Position</Label>
              <Select value={newPosition} onValueChange={setNewPosition}>
                <SelectTrigger>
                  <SelectValue placeholder="Position auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">Rolle</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Rolle auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Benutzer</SelectItem>
                  <SelectItem value="co-admin">Co-Administrator</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  {currentUser?.role === "developer" && (
                    <SelectItem value="developer">Entwickler</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewUserOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={isCreatingUser}
            >
              {isCreatingUser ? "Wird erstellt..." : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>
              Ändern Sie die Informationen des Benutzers.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-position">Position</Label>
              <Select value={editPosition} onValueChange={setEditPosition}>
                <SelectTrigger>
                  <SelectValue placeholder="Position auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-role">Rolle</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Rolle auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Benutzer</SelectItem>
                  <SelectItem value="co-admin">Co-Administrator</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                  {currentUser?.role === "developer" && (
                    <SelectItem value="developer">Entwickler</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-status">Status</Label>
              <Select value={editActive ? "active" : "inactive"} onValueChange={(value) => setEditActive(value === "active")}>
                <SelectTrigger>
                  <SelectValue placeholder="Status auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="inactive">Deaktiviert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateUser}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete/Deactivate User Dialog */}
      <Dialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.active === false ? "Benutzer aktivieren" : "Benutzer deaktivieren"}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.active === false
                ? "Sind Sie sicher, dass Sie diesen Benutzer aktivieren möchten? Der Benutzer kann sich nach der Aktivierung wieder anmelden."
                : "Sind Sie sicher, dass Sie diesen Benutzer deaktivieren möchten? Der Benutzer kann sich nach der Deaktivierung nicht mehr anmelden."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant={selectedUser?.active === false ? "default" : "destructive"}
              onClick={handleDeleteUser}
            >
              {selectedUser?.active === false ? "Aktivieren" : "Deaktivieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invitation Activation Dialog */}
      <InvitationActivationDialog
        open={activationDialogOpen}
        onOpenChange={setActivationDialogOpen}
        invitation={selectedInvitation}
        inviterName={selectedInvitation ? (inviterNames[selectedInvitation.inviterUserId] || "Unbekannter Benutzer") : ""}
        onActivate={handleActivateInvitation}
        isActivating={isActivatingInvitation}
      />
    </div>
  );
}
